import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Container, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SearchBar from '../../Components/search_bar.tsx';
import CategoryButton from '../../Components/CategoryButton/category_button.tsx';
import ProductGrid from '../../Components/ProductGrid/product_grid.tsx';
import BottomNavbar from '../../Components/bottom_navbar.tsx';
import CartScreen from './cart.tsx';
import OrderSummaryScreen from './order_summary.tsx';
import BuyAgainScreen from './buy_again.tsx';
import YourOrders from './your_orders.tsx';
import './offers.scss';
import { fetchWooProductsPaginated } from './action.ts';
import NavBar from "../../../WebApp/Components/Navbar/sidenav.tsx";
import Header from "../../../WebApp/Components/Header/header.tsx";


interface Product {
  id: number;
  name: string;
  category: string;
  tags: string[];
  price: number;
  image: string;
  quantity?: number;
}

const OffersScreen: React.FC = () => {
  const [urlParams, setUrlParams] = useState({
    token: '',
    userId: '',
    userName: '',
    userRole: '',
    userPhone: '',
    appVersion: '',
    vehicleId: '',
  });

  const [selectedCategoryName, setSelectedCategoryName] = useState('All');
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartItems, setCartItems] = useState<Product[]>([]);
  const [orderItems, setOrderItems] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<'home' | 'cart' | 'order-summary' | 'buy-again' | 'order-placed' | 'your-orders'>('home');
  const [wasOrderPlaced, setWasOrderPlaced] = useState(false);
  const navigate = useNavigate();
  const prevTab = useRef(activeTab);
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);

  //  Fetch products from WooCommerce
  const loadMoreProducts = useCallback(() => {
    if (loadingProducts || !hasNextPage) return;
    setLoadingProducts(true);
 fetchWooProductsPaginated(
    page,
    (data) => {
      console.log('Products returned:', data.products.length);
      setProducts(prevProducts => {
        const existingIds = new Set(prevProducts.map(p => p.id));
        const filteredNew = data.products.filter(p => !existingIds.has(p.id));
        return [...prevProducts, ...filteredNew];
      });
      setHasNextPage(data.hasNextPage);
      setPage(prev => prev + 1);
      setLoadingProducts(false);
    },
    (error) => {
      toast.error(error?.message || error || 'Error fetching products.');
      setLoadingProducts(false);
    },
    selectedCategoryId
  );

  }, [page, hasNextPage, loadingProducts, selectedCategoryId]);

  //  Fetch on mount & when category ID changes
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasNextPage(true);
  }, [selectedCategoryId]);

  useEffect(() => {
    loadMoreProducts();
  }, [selectedCategoryId]);

const scrollRef = useRef<HTMLDivElement>(null);

useEffect(() => {
  const handleScroll = (target: HTMLElement | Window) => {
    let scrollTop: number, clientHeight: number, scrollHeight: number;

    if (target === window) {
      scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      clientHeight = window.innerHeight;
      scrollHeight = document.documentElement.scrollHeight;
    } else {
      const el = target as HTMLElement;
      scrollTop = el.scrollTop;
      clientHeight = el.clientHeight;
      scrollHeight = el.scrollHeight;
    }

    if (scrollTop + clientHeight + 200 >= scrollHeight) {
      loadMoreProducts();
    }
  };

  const winHandler = () => handleScroll(window);
  window.addEventListener("scroll", winHandler);

  const el = scrollRef.current;
  let boxHandler: (() => void) | null = null;

  if (el) {
    boxHandler = () => handleScroll(el);
    el.addEventListener("scroll", boxHandler);
  }

  return () => {
    window.removeEventListener("scroll", winHandler);
    if (el && boxHandler) el.removeEventListener("scroll", boxHandler);
  };
}, [loadMoreProducts]);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('order_status') === 'paid') {
      setActiveTab('order-placed');
      setCartItems([]);
    }
  }, []);

  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('trackyo_cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          setCartItems(parsedCart);
        } else {
          toast.warn('Cart data is corrupted.');
        }
      }
    } catch (e) {
      toast.error('Error loading cart from localStorage.');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('trackyo_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    let userData = {
      token: queryParams.get('token') || '',
      userId: queryParams.get('user_id') || '',
      userName: queryParams.get('user_name') || '',
      userRole: queryParams.get('user_role') || '',
      userPhone: queryParams.get('user_phone') || '',
      appVersion: queryParams.get('app_version') || '',
      vehicleId: queryParams.get('vehicle_id')|| '',
    };
    // If vendor_supervisor → override from localStorage
    const userDetails = localStorage.getItem("userdetails");
    if (userDetails) {
      const parsed = JSON.parse(userDetails);
      if (parsed?.role?.toLowerCase() === "vendor_supervisor"    ) {
        userData.userRole = "vendor_supervisor";
       
      } else if (parsed?.role?.toLowerCase() === 'client_company_admin')
      {
          userData.userRole = "client_company_admin";
      }
      else if (parsed?.role?.toLowerCase() === 'vendor_company_admin'){
          userData.userRole = "vendor_company_admin";
      }
       userData.userName = parsed.name || userData.userName;
        userData.userPhone = parsed.phone || userData.userPhone;
    }

    // Save in localStorage for persistence
    if (userData.userName) {
      localStorage.setItem('trackyo_user', JSON.stringify(userData));
    } else {
      const saved = localStorage.getItem('trackyo_user');
      if (saved) {
        const savedData = JSON.parse(saved);
        if (!userData.userPhone && savedData.userPhone) {
          userData.userPhone = savedData.userPhone;
        }
      }
    }

    setUrlParams(userData);
  }, []);
const isVendorSupervisor = ["vendor_supervisor", "vendor_company_admin", "client_company_admin"].includes(urlParams.userRole);
console.log(isVendorSupervisor)
  useEffect(() => {
    if (
      wasOrderPlaced &&
      prevTab.current === 'order-placed' &&
      activeTab !== 'order-placed'
    ) {
      setCartItems([]);
      localStorage.removeItem('trackyo_cart');
      setWasOrderPlaced(false);
    }
    prevTab.current = activeTab;
  }, [activeTab, wasOrderPlaced]);

const handleCategoryChange = (name: string, id: number) => {
  setSelectedCategoryName(name);
  setSelectedCategoryId(id);
  setSearchTerm('');
  setProducts([]);
  setPage(1);
  setHasNextPage(true);
};
  const handleSearch = (term: string) => setSearchTerm(term);
  const handleBack = () => {
    if (!urlParams) return;
    const { userName, vehicleId, userId, token } = urlParams;
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  };

  const handleAddToCart = (product: Product) => {
    setCartItems((prevCart) => {
      const existing = prevCart.find((item) => item.id === product.id);
      if (existing) {
        return prevCart.map((item) =>
          item.id === product.id ? { ...item, quantity: (item.quantity || 1) + 1 } : item
        );
      } else {
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });
  };

  const handleRemoveFromCart = (product: Product) => {
    setCartItems((prevCart) =>
      prevCart
        .map((item) =>
          item.id === product.id ? { ...item, quantity: (item.quantity || 1) - 1 } : item
        )
        .filter((item) => item.quantity && item.quantity > 0)
    );
  };

  const handleDeleteFromCart = (product: Product) => {
    setCartItems((prevCart) => prevCart.filter(item => item.id !== product.id));
  };

  const handleOrderSummary = () => {
    setOrderItems(cartItems);
    setActiveTab('order-summary');
  };

  const totalCartQuantity = cartItems.reduce((acc, item) => acc + (item.quantity || 0), 0);

const filteredProducts = products.filter(product =>
 product.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
);
if (isVendorSupervisor) {
  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          zIndex: 1200,
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <Header />
      </Box>

      <Box
        sx={{
          marginLeft: { xs: 0, sm: "180px" }, 
          marginTop: "60px",                
          marginRight: "10px",
          background: "#f8fafb",
          display: "flex",
          flexDirection: "column",
          flex: 1,         
          minHeight: 0,    
          overflowY: activeTab === "home" ? "auto" : "hidden",
          height: "calc(100vh - 70px)", 
        }}
      >

        {activeTab === "home" && (
          <Box
            sx={{
              position: "sticky",
              top: 0, 
              zIndex: 1100,
              background: "#fff",
              pb: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            <Box mt={{ xs: 1, sm: 0 }} mb={1}>
              <SearchBar value={searchTerm} onChange={setSearchTerm} />
            </Box>
            <Box mb={{ xs: 1, sm: 1 }}>
              <CategoryButton
                selected={selectedCategoryName}
                onSelect={handleCategoryChange}
                userRole={urlParams.userRole}
              />
            </Box>
          </Box>
        )}



            {activeTab === "home" && (
                      <Box
        ref={scrollRef}
          sx={{
            flex: 1, 
            minHeight: 0,           
            overflowY: "auto",
            pt: { xs: 1, sm: 1 },
            pb: "calc(56px + 20px)",
            pr:"10px",
            pl:"10px",
          }}
        >
              
                <ProductGrid
                  category={selectedCategoryName}
                  search={searchTerm}
                  onAddToCart={handleAddToCart}
                  onRemove={handleRemoveFromCart}
                  cartItems={cartItems}
                  products={filteredProducts}
                />
                {loadingProducts && (
                  <Box sx={{ textAlign: "center", py: 2 }}>Loading more products...</Box>
                )}
                {!hasNextPage && filteredProducts.length > 0 && (
                  <Box sx={{ textAlign: "center", py: 2 }}>No more products</Box>
                )}
                {!loadingProducts && filteredProducts.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 2 }}>No products found.</Box>
                )}
              </Box>
            )}

          {activeTab === "cart" && (
            <CartScreen
              cartItems={cartItems}
              onAdd={id => {
                const product = cartItems.find(p => p.id === id);
                if (product) handleAddToCart(product);
              }}
              onRemove={id => {
                const product = cartItems.find(p => p.id === id);
                if (product) handleRemoveFromCart(product);
              }}
              onDelete={id => {
                const product = cartItems.find(p => p.id === id);
                if (product) handleDeleteFromCart(product);
              }}
              onBack={() => setActiveTab("home")}
              onPlaceOrder={handleOrderSummary}
              placeOrderLabel="Order Summary"
              userRole={urlParams.userRole}
            />
          )}

          {activeTab === "order-summary" && (
            <OrderSummaryScreen
              cartItems={orderItems}
              onBackToCart={() => setActiveTab("cart")}
              onPlaceOrder={() => {
                const purchasedItems = [...orderItems];
                const existing = localStorage.getItem("trackyo_past_purchases");
                const existingParsed = existing ? JSON.parse(existing) : [];
                const merged = [...existingParsed];
                purchasedItems.forEach(item => {
                  if (!merged.find(p => p.id === item.id)) merged.push(item);
                });
                localStorage.setItem("trackyo_past_purchases", JSON.stringify(merged));
                setWasOrderPlaced(true);
                setCartItems([]);
                localStorage.removeItem("trackyo_cart");
                setActiveTab("buy-again");
              }}
            />
          )}

          {activeTab === "buy-again" && (
            <BuyAgainScreen
              cartItems={cartItems}
              onAdd={handleAddToCart}
              onRemove={handleRemoveFromCart}
              onBack={() => setActiveTab("home")}
              userPhone={urlParams.userPhone}
              userRole={urlParams.userRole}
            />
          )}

          {activeTab === "your-orders" && (
            <YourOrders
              userPhone={urlParams.userPhone}
              onBack={() => setActiveTab("home")}
              userRole={urlParams.userRole}
            />
          )}
        {totalCartQuantity > 0 && activeTab === 'home' && (
  <Box 
    className="view-cart-button-floating" 
    onClick={() => setActiveTab('cart')}
    sx={{ 
      position: 'fixed', 
      bottom: '70px', // Position it above the bottom navbar
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1300,
      width: { xs: '0%', sm: '200px' } 
    }}
  >
    <Box className="view-cart-row1">
      <ShoppingCartIcon className="view-cart-icon" />
      <span className="view-cart-text">View Cart</span>
      <ArrowForwardIosIcon className="view-cart-arrow" />
    </Box>
    <Box className="view-cart-row2">
      <span className="view-cart-count">
        {totalCartQuantity} Item{totalCartQuantity > 1 ? 's' : ''}
      </span>
    </Box>
  </Box>
)}
        
      <Box
        sx={{
          position: "sticky", 
          bottom: 0,
          background: "#fff",
          borderTop: "1px solid #eee",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
          height: "56px",
          display: "flex",
          alignItems: "center",
          mt: "auto" 
        }}
      >
        <BottomNavbar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          cartCount={totalCartQuantity}
        />
      </Box>
      </Box>

      <NavBar selected="Offers" />
    </>
  );
}

  return (
    <Container maxWidth="sm" sx={{ paddingX: 2, paddingBottom: '90px' }}>
      {activeTab === 'home' && (
        <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: '#fff',
          zIndex: 100,
          pt: '30px',
        }}
      >
        <Box display="flex" alignItems="center" gap={1} pt={1}>
          <IconButton onClick={handleBack} sx={{ padding: 1 }}>
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Box flex={1}>
            <SearchBar value={searchTerm} onChange={handleSearch} />
          </Box>
        </Box>

        <Box mt={0.5}>
          <CategoryButton selected={selectedCategoryName} onSelect={handleCategoryChange} />
        </Box>
      </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={1}>
            <Typography fontWeight={400} fontSize={15} color="#555" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              {urlParams.userName
                ? `Hi ${urlParams.userName}, Your Quick Picks`
                : 'HEY THERE, YOUR QUICK PICKS'}
            </Typography>
          </Box>

          <ProductGrid
            category={selectedCategoryName}
            search={searchTerm}
            onAddToCart={handleAddToCart}
            onRemove={handleRemoveFromCart}
            cartItems={cartItems}
            products={filteredProducts}
          />

          {loadingProducts && (
            <div style={{ textAlign: 'center', padding: 8 }}>Loading more products...</div>
          )}
          {!hasNextPage && products.length > 0 && (
            <div style={{ textAlign: 'center', padding: 8 }}>No more products</div>
          )}
          {!loadingProducts && products.length === 0 && (
            <div style={{ textAlign: 'center', padding: 8 }}>No products found.</div>
          )}

          {totalCartQuantity > 0 && (
            <Box className="view-cart-button-floating" onClick={() => setActiveTab('cart')}>
              <Box className="view-cart-row1">
                <ShoppingCartIcon className="view-cart-icon" />
                <span className="view-cart-text">View Cart</span>
                <ArrowForwardIosIcon className="view-cart-arrow" />
              </Box>
              <Box className="view-cart-row2">
                <span className="view-cart-count">
                  {totalCartQuantity} Item{totalCartQuantity > 1 ? 's' : ''}
                </span>
              </Box>
            </Box>
          )}
        </>
      )}

      {activeTab === 'cart' && (
        <CartScreen
          cartItems={cartItems}
          onAdd={(id) => {
            const product = cartItems.find(p => p.id === id);
            if (product) handleAddToCart(product);
          }}
          onRemove={(id) => {
            const product = cartItems.find(p => p.id === id);
            if (product) handleRemoveFromCart(product);
          }}
          onDelete={(id) => {
            const product = cartItems.find(p => p.id === id);
            if (product) handleDeleteFromCart(product);
          }}
          onBack={() => setActiveTab('home')}
          onPlaceOrder={handleOrderSummary}
          placeOrderLabel="Order Summary"
        />
      )}

      {activeTab === 'order-summary' && (
        <OrderSummaryScreen
          cartItems={orderItems}
          onBackToCart={() => setActiveTab('cart')}
          onPlaceOrder={() => {
          const purchasedItems = [...orderItems]; 

          const existing = localStorage.getItem('trackyo_past_purchases');
          const existingParsed: Product[] = existing ? JSON.parse(existing) : [];

          const merged = [...existingParsed];

          purchasedItems.forEach((item) => {
            if (!merged.find(p => p.id === item.id)) {
              merged.push(item);
            }
          });

          localStorage.setItem('trackyo_past_purchases', JSON.stringify(merged));

          setWasOrderPlaced(true);
          setCartItems([]);
          localStorage.removeItem('trackyo_cart');
          setActiveTab('buy-again');
         }}
        />
      )}

      {activeTab === 'buy-again' && (
        <BuyAgainScreen
          cartItems={cartItems}
          onAdd={handleAddToCart}
          onRemove={handleRemoveFromCart}
          onBack={() => setActiveTab('home')}
          userPhone={urlParams.userPhone}
        />
      )}
      {activeTab === 'your-orders' && (
        <YourOrders
          userPhone={urlParams.userPhone}
          onBack={() => setActiveTab('home')}
        />
      )}

      <BottomNavbar
      activeTab={activeTab}
      onTabChange={setActiveTab}
      cartCount={totalCartQuantity}
    />
    </Container>
  );
};

export default OffersScreen;

